import React, { useState } from 'react';
import { X, Users, Plus, ShieldCheck, Check } from 'lucide-react';
import { ProjectTask, ProjectRoleAssignment, CORE_ROLE_PRESETS, TeamMemberProfile } from './types.ts';
import { TEAM_MEMBERS } from './initialProjects.ts';

interface QuickAddRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: ProjectTask | null;
  onAddRole: (projectId: string, role: ProjectRoleAssignment) => void;
  teamMembers?: TeamMemberProfile[];
}

export const QuickAddRoleModal: React.FC<QuickAddRoleModalProps> = ({
  isOpen,
  onClose,
  project,
  onAddRole,
  teamMembers = TEAM_MEMBERS
}) => {
  const [selectedPresetIndex, setSelectedPresetIndex] = useState<number>(4); // Testing & Quality preset default
  const [customRoleTitle, setCustomRoleTitle] = useState('');
  const [customDepartment, setCustomDepartment] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(teamMembers[0]?.name || 'Lakhwinder');

  if (!isOpen || !project) return null;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();

    let roleTitle = customRoleTitle.trim();
    let department = customDepartment.trim();
    let badgeColor = 'emerald';

    if (!roleTitle) {
      const preset = CORE_ROLE_PRESETS[selectedPresetIndex];
      if (preset) {
        roleTitle = preset.role;
        department = preset.department;
        badgeColor = preset.badgeColor;
      } else {
        roleTitle = 'Testing & Quality Incharge';
        department = 'Quality';
      }
    }

    const newRole: ProjectRoleAssignment = {
      id: `role-${Date.now()}`,
      role: roleTitle,
      person: selectedPerson || teamMembers[0]?.name || 'Unassigned',
      department: department || 'Operations',
      badgeColor
    };

    onAddRole(project.id, newRole);
    setCustomRoleTitle('');
    setCustomDepartment('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-60 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-5 py-3.5 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="p-1.5 bg-blue-600/60 rounded-lg">
              <Users className="w-5 h-5 text-blue-200" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-white flex items-center space-x-1.5">
                <span>Add Core Incharge Role</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-500/30 text-blue-200 border border-blue-400/30">
                  DVEPL-{project.dveplCode}
                </span>
              </h3>
              <p className="text-[11px] text-slate-300">
                Will be displayed on the main page under "Project Concerned Persons"
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSave} className="p-5 space-y-4 text-xs">
          {/* Preset Roles Picker */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
              Choose Pre-Configured Incharge Role:
            </label>
            <div className="grid grid-cols-2 gap-2">
              {CORE_ROLE_PRESETS.map((preset, idx) => (
                <button
                  key={preset.role}
                  type="button"
                  onClick={() => {
                    setSelectedPresetIndex(idx);
                    setCustomRoleTitle('');
                  }}
                  className={`p-2 rounded-xl border text-left transition-all cursor-pointer ${
                    selectedPresetIndex === idx && !customRoleTitle
                      ? 'bg-blue-50 border-blue-400 text-blue-950 font-bold ring-2 ring-blue-100'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <p className="text-xs font-semibold leading-tight">{preset.role}</p>
                  <p className="text-[10px] text-slate-500">{preset.department}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Or Custom Role */}
          <div className="pt-2 border-t border-slate-100">
            <label className="block text-[11px] font-bold text-slate-700 mb-1">
              Or Custom Incharge Role Title:
            </label>
            <input
              type="text"
              value={customRoleTitle}
              onChange={e => setCustomRoleTitle(e.target.value)}
              placeholder="e.g., Laser Cutting Incharge, Site Foundation Incharge"
              className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          {/* Assign Team Member */}
          <div>
            <label className="block text-[11px] font-bold text-slate-700 mb-1">
              Assign Responsible Team Member:
            </label>
            <select
              value={selectedPerson}
              onChange={e => setSelectedPerson(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-white font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              {teamMembers.map(tm => (
                <option key={tm.id || tm.name} value={tm.name}>
                  {tm.name} ({tm.department || tm.role})
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors cursor-pointer"
            >
              <Check className="w-4 h-4 mr-1.5 stroke-[2.5]" />
              <span>Save &amp; Display on Main Page</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
